import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { UserFeatureSelector } from '@/components/UserFeatureSelector';
import PremiumStatsCard from '@/components/PremiumStatsCard';
import Pinboard from '@/components/Pinboard';
import { 
  Leaf, Sparkles, BookOpen, Target, Heart, Brain, Mountain, Compass, Settings,
  Flame, TrendingUp, Calendar, CheckCircle2, Play, Clock, Award, ChevronRight,
  Zap, Star, Trophy, Shield, HeartOff, FolderOpen, Music, Dumbbell, Pin, Eye, EyeOff
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const DynamicHomePage = () => {
  const { t, i18n } = useTranslation();
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [isFeatureDialogOpen, setIsFeatureDialogOpen] = useState(false);
  const [isFeatureSelectorOpen, setIsFeatureSelectorOpen] = useState(false);
  const [userFeatures, setUserFeatures] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [userPractices, setUserPractices] = useState([]);
  const [dailyQuote, setDailyQuote] = useState('');
  const [isQuoteFavorite, setIsQuoteFavorite] = useState(false);
  const [pricingConfig, setPricingConfig] = useState(null);
  
  // Section visibility states - load from localStorage with defaults
  const [sectionVisibility, setSectionVisibility] = useState(() => {
    const saved = localStorage.getItem('homepageSectionVisibility');
    return saved ? JSON.parse(saved) : {
      quote: true,
      stats: true,
      premium_stats: true,
      motivation: true,
      practices: true,
      quick_access: true,
      pinboard: true
    };
  });

  const [userStats, setUserStats] = useState({
    totalSessions: 0,
    currentStreak: 0,
    longestStreak: 0,
    activePractices: 0
  });

  // Toggle section visibility
  const toggleSection = (sectionId) => {
    const newVisibility = {
      ...sectionVisibility,
      [sectionId]: !sectionVisibility[sectionId]
    };
    setSectionVisibility(newVisibility);
    localStorage.setItem('homepageSectionVisibility', JSON.stringify(newVisibility));
  };

  // Section labels for toggle buttons
  const sectionLabels = {
    quote: { de: 'Tagesspruch', en: 'Daily Quote', icon: '💬' },
    stats: { de: 'Statistiken', en: 'Statistics', icon: '📊' },
    premium_stats: { de: 'Dein Fortschritt', en: 'Your Progress', icon: '📈' },
    motivation: { de: 'Tägliche Motivation', en: 'Daily Motivation', icon: '🔥' },
    practices: { de: 'Praktiken', en: 'Practices', icon: '🧘' },
    quick_access: { de: 'Schnellzugriff', en: 'Quick Access', icon: '⚡' },
    pinboard: { de: 'Meine Pinwand', en: 'My Pinboard', icon: '📌' }
  };

  // Helper function to get text in current language
  const getText = (textObj) => {
    if (!textObj) return '';
    if (typeof textObj === 'string') return textObj;
    const currentLang = i18n.language?.startsWith('de') ? 'de' : 'en';
    return textObj[currentLang] || textObj.de || textObj.en || '';
  };

  // Section Toggle Button Component - still available as standalone
  const SectionToggle = ({ sectionId, className = '' }) => {
    const label = sectionLabels[sectionId];
    const isVisible = sectionVisibility[sectionId];
    const currentLang = i18n.language?.startsWith('de') ? 'de' : 'en';
    
    return (
      <motion.button
        onClick={() => toggleSection(sectionId)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
          isVisible 
            ? 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300' 
            : 'bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-500 dark:text-slate-400'
        } ${className}`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <span>{label?.icon}</span>
        <span className="font-medium">{label?.[currentLang] || label?.de}</span>
        {isVisible ? (
          <EyeOff className="h-3.5 w-3.5 ml-1 opacity-60" />
        ) : (
          <Eye className="h-3.5 w-3.5 ml-1 opacity-60" />
        )}
      </motion.button>
    );
  };

  // Clickable Section Header - toggle visibility directly in title
  const SectionHeader = ({ sectionId, title, description, icon: Icon, className = '', children }) => {
    const isVisible = sectionVisibility[sectionId];
    
    return (
      <div className={`mb-4 ${className}`}>
        <div className="flex items-center justify-between">
          <motion.button
            onClick={() => toggleSection(sectionId)}
            className="flex items-center gap-2 group cursor-pointer"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            {Icon && <Icon className="h-5 w-5 text-[#006039] dark:text-emerald-400" />}
            <h2 className="text-xl font-serif font-bold text-foreground group-hover:text-[#006039] transition-colors">
              {title}
            </h2>
            <motion.div 
              className="ml-2 p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              whileHover={{ scale: 1.1 }}
            >
              {isVisible ? (
                <EyeOff className="h-4 w-4 text-slate-400 group-hover:text-slate-600" />
              ) : (
                <Eye className="h-4 w-4 text-slate-400 group-hover:text-slate-600" />
              )}
            </motion.div>
          </motion.button>
          {children}
        </div>
        {description && (
          <p className="text-sm text-muted-foreground mt-1 ml-7">
            {description}
          </p>
        )}
      </div>
    );
  };

  const handleFeatureClick = (feature) => {
    if (feature.extended_description && getText(feature.extended_description)) {
      setSelectedFeature(feature);
      setIsFeatureDialogOpen(true);
    }
  };

  // Principles from backend config with fallback
  const principles = (config?.principles_section?.principles || []).map(p => ({
    icon: { Leaf, Heart, Brain, Sparkles, Shield, Star, Mountain, BookOpen, Target, Zap }[p.icon] || Leaf,
    title: getText(p.title) || p.title,
    description: getText(p.description) || p.description
  }));
  if (principles.length === 0) {
    principles.push(
      { icon: Leaf, title: t('home.principles.simplicity.title'), description: t('home.principles.simplicity.description') },
      { icon: Heart, title: t('home.principles.mindfulness.title'), description: t('home.principles.mindfulness.description') },
      { icon: Brain, title: t('home.principles.discipline.title'), description: t('home.principles.discipline.description') },
      { icon: Sparkles, title: t('home.principles.peace.title'), description: t('home.principles.peace.description') }
    );
  }

  // Practices from backend config with fallback
  const practices = (config?.practices_section?.practices || []).map(p => ({
    title: getText(p.title) || p.title,
    duration: getText(p.duration) || p.duration,
    difficulty: getText(p.difficulty) || p.difficulty,
    description: getText(p.description) || p.description
  }));
  if (practices.length === 0) {
    practices.push(
      { title: i18n.language === 'de' ? 'Morgenmeditation' : 'Morning Meditation', duration: '15 min', difficulty: i18n.language === 'de' ? 'Anfänger' : 'Beginner', description: i18n.language === 'de' ? 'Starte Deinen Tag mit Klarheit und Ruhe.' : 'Start your day with clarity and calm.' },
      { title: i18n.language === 'de' ? 'Minimalismus-Challenge' : 'Minimalist Challenge', duration: i18n.language === 'de' ? '30 Tage' : '30 days', difficulty: i18n.language === 'de' ? 'Fortgeschritten' : 'Intermediate', description: i18n.language === 'de' ? 'Reduziere schrittweise Besitz und entdecke, was wirklich zählt.' : 'Gradually reduce possessions and discover what truly matters.' }
    );
  }

  // Stats from backend config with fallback
  const stats = (config?.stats_section?.stats || []).map(s => ({
    value: s.value,
    label: getText(s.label) || s.label
  }));
  if (stats.length === 0) {
    stats.push(
      { value: '1000+', label: t('home.stats.practitioners') },
      { value: '50+', label: t('home.stats.practices') },
      { value: '95%', label: t('home.stats.satisfaction') },
      { value: '24/7', label: t('home.stats.support') }
    );
  }

  useEffect(() => {
    // Load critical data first, then user-specific data
    const loadData = async () => {
      try {
        // Critical: Homepage config (needed for rendering)
        await fetchConfig();
        
        // Check login status
        checkLogin();
        
        // Non-blocking: Load additional data in parallel
        Promise.all([
          fetchDailyQuote(),
          fetchPricingConfig()
        ]).catch(err => console.error('Error loading additional data:', err));
      } catch (error) {
        console.error('Error loading homepage:', error);
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  const fetchPricingConfig = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/pricing/config`
      );
      if (response.ok) {
        const data = await response.json();
        setPricingConfig(data);
      }
    } catch (error) {
      console.error('Error fetching pricing config:', error);
    }
  };

  const fetchDailyQuote = async () => {
    try {
      const currentLang = i18n.language?.startsWith('de') ? 'de' : 'en';
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/homepage-config/daily-quote?lang=${currentLang}`
      );
      if (response.ok) {
        const data = await response.json();
        setDailyQuote(data.quote);
        
        // Check if this quote is a favorite (only for logged in users)
        const token = localStorage.getItem('token');
        if (token && data.quote) {
          checkQuoteFavorite(data.quote);
        }
      }
    } catch (error) {
      console.error('Error fetching daily quote:', error);
    }
  };

  const checkQuoteFavorite = async (quote) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/homepage-config/favorite-quotes/check`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ quote })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setIsQuoteFavorite(data.is_favorite);
      }
    } catch (error) {
      console.error('Error checking favorite:', error);
    }
  };

  const toggleQuoteFavorite = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error(i18n.language?.startsWith('de') ? 'Bitte einloggen' : 'Please login');
      return;
    }
    
    if (!dailyQuote) return;
    
    try {
      if (isQuoteFavorite) {
        // Remove from favorites - we need to find the index first
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/homepage-config/favorite-quotes`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (response.ok) {
          const data = await response.json();
          const index = data.favorites.findIndex(f => f.quote === dailyQuote);
          
          if (index >= 0) {
            await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/homepage-config/favorite-quotes/${index}`,
              {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
              }
            );
            setIsQuoteFavorite(false);
            toast.success(i18n.language?.startsWith('de') ? 'Aus Favoriten entfernt' : 'Removed from favorites');
          }
        }
      } else {
        // Add to favorites
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/homepage-config/favorite-quotes`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ quote: dailyQuote })
          }
        );
        
        if (response.ok) {
          setIsQuoteFavorite(true);
          toast.success(i18n.language?.startsWith('de') ? 'Zu Favoriten hinzugefügt' : 'Added to favorites');
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Error');
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error(i18n.language?.startsWith('de') ? 'Fehler' : 'Error');
    }
  };

  const checkLogin = () => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    setIsLoggedIn(!!token);
    if (token) {
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          setUserName(user.first_name || user.full_name?.split(' ')[0] || user.email?.split('@')[0] || '');
        } catch (e) {
          console.error('Error parsing user:', e);
        }
      }
      fetchUserFeatures();
      fetchUserPractices();
    }
  };

  const fetchUserPractices = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8s timeout
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/practices/tracking`,
        { 
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        }
      );
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        setUserPractices(data);
        
        // Calculate stats
        let totalSessions = 0;
        let maxStreak = 0;
        let currentMaxStreak = 0;
        
        data.forEach(practice => {
          totalSessions += practice.total_sessions || 0;
          if (practice.longest_streak > maxStreak) {
            maxStreak = practice.longest_streak;
          }
          if (practice.current_streak > currentMaxStreak) {
            currentMaxStreak = practice.current_streak;
          }
        });
        
        setUserStats({
          totalSessions,
          currentStreak: currentMaxStreak,
          longestStreak: maxStreak,
          activePractices: data.filter(p => p.is_active).length
        });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching practices:', error);
      }
    }
  };

  const fetchUserFeatures = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8s timeout
      
      // Fetch available features
      const availableResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/homepage-prefs/available-features`,
        { signal: controller.signal }
      );
      
      if (!availableResponse.ok) {
        clearTimeout(timeoutId);
        return;
      }
      const availableData = await availableResponse.json();
      
      // Fetch user preferences
      const prefsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/homepage-prefs`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        }
      );
      clearTimeout(timeoutId);
      
      if (prefsResponse.ok) {
        const prefsData = await prefsResponse.json();
        const selectedIds = prefsData.selected_features || [];
        
        // Filter available features by selected IDs
        const selectedFeatures = availableData.features.filter(f => 
          selectedIds.includes(f.id)
        );
        setUserFeatures(selectedFeatures);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching user features:', error);
      }
    }
  };

  const fetchConfig = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/homepage-config`,
        { signal: controller.signal }
      );
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      } else {
        // Set default config to prevent infinite loading
        console.warn('Homepage config not found, using defaults');
        setConfig({
          hero: {},
          features: [],
          logged_in_config: {}
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Homepage config fetch timeout');
      } else {
        console.error('Error fetching homepage config:', error);
      }
      // Set default config to prevent infinite loading
      setConfig({
        hero: {},
        features: [],
        logged_in_config: {}
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    const loggedInConfig = config?.logged_in_config;
    const greeting = loggedInConfig?.greeting;
    
    if (hour < 12) {
      return getText(greeting?.morning_greeting) || (i18n.language?.startsWith('de') ? 'Guten Morgen' : 'Good Morning');
    }
    if (hour < 18) {
      return getText(greeting?.afternoon_greeting) || (i18n.language?.startsWith('de') ? 'Guten Tag' : 'Good Afternoon');
    }
    return getText(greeting?.evening_greeting) || (i18n.language?.startsWith('de') ? 'Guten Abend' : 'Good Evening');
  };

  // Default section order - premium_stats replaces simple stats for richer dashboard
  const DEFAULT_SECTION_ORDER = ['quote', 'premium_stats', 'motivation', 'practices', 'quick_access'];

  // Get logged-in config values with fallbacks
  const getLoggedInConfig = () => {
    const lic = config?.logged_in_config || {};
    return {
      greetingSubtitle: getText(lic.greeting?.subtitle) || (i18n.language?.startsWith('de') ? 'Bereit für Deine heutige Praxis?' : 'Ready for your daily practice?'),
      ctaButtonText: getText(lic.greeting?.cta_button_text) || (i18n.language?.startsWith('de') ? 'Praktiken starten' : 'Start Practice'),
      motivationBadge: getText(lic.motivation_banner?.badge_text) || (i18n.language?.startsWith('de') ? 'Tägliche Motivation' : 'Daily Motivation'),
      motivationTitle: getText(lic.motivation_banner?.title) || (i18n.language?.startsWith('de') ? 'Jeder Tag ist eine neue Chance zu wachsen.' : 'Every day is a new chance to grow.'),
      motivationDescription: getText(lic.motivation_banner?.description) || (i18n.language?.startsWith('de') ? 'Kleine, tägliche Schritte führen zu großen Veränderungen. Bleib dran und feiere jeden Fortschritt!' : 'Small daily steps lead to big changes. Stay consistent and celebrate every progress!'),
      primaryButtonText: getText(lic.motivation_banner?.primary_button_text) || (i18n.language?.startsWith('de') ? 'Tagebuch' : 'Journal'),
      primaryButtonLink: lic.motivation_banner?.primary_button_link || '/journal',
      secondaryButtonText: getText(lic.motivation_banner?.secondary_button_text) || (i18n.language?.startsWith('de') ? 'Fortschritt' : 'Progress'),
      secondaryButtonLink: lic.motivation_banner?.secondary_button_link || '/progress',
      showPractices: lic.show_practices_section !== false,
      practicesTitle: getText(lic.practices_section_title) || (i18n.language?.startsWith('de') ? 'Deine Praktiken' : 'Your Practices'),
      showQuickAccess: lic.show_quick_access !== false,
      quickAccessTitle: getText(lic.quick_access_title) || (i18n.language?.startsWith('de') ? 'Schnellzugriff' : 'Quick Access'),
      sectionOrder: lic.section_order || DEFAULT_SECTION_ORDER,
      // Dynamic CTA buttons
      ctaButtons: lic.greeting?.cta_buttons || [],
      // Pinboard settings
      showPinboard: lic.pinboard?.enabled || false,
      pinboardTitle: getText(lic.pinboard?.title) || (i18n.language?.startsWith('de') ? 'Meine Pinwand' : 'My Pinboard'),
      pinboardDescription: getText(lic.pinboard?.description) || '',
    };
  };

  if (isLoading || !config) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Lade Homepage...</p>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // LOGGED IN USER VIEW - Personal Dashboard
  // ========================================
  if (isLoggedIn) {
    const lic = getLoggedInConfig();
    
    // Section render functions
    const renderQuoteSection = () => (
      dailyQuote && (
        <div key="quote-section" className="mb-6">
          <SectionHeader 
            sectionId="quote" 
            title={i18n.language?.startsWith('de') ? 'Tagesspruch' : 'Daily Quote'}
            icon={Sparkles}
          />
          {sectionVisibility.quote && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-gradient-to-br from-[#006039]/5 via-[#00915a]/5 to-transparent border-[#006039]/20 dark:border-[#006039]/30 overflow-hidden relative">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#006039] via-[#00915a] to-[#006039]" />
                <CardContent className="p-5 sm:p-6">
                  <div className="flex items-start gap-4">
                    <div className="hidden sm:flex p-3 rounded-xl bg-gradient-to-br from-[#006039]/15 to-[#00915a]/10 shrink-0">
                      <Sparkles className="h-6 w-6 text-[#006039] dark:text-emerald-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <Sparkles className="sm:hidden h-4 w-4 text-[#006039] dark:text-emerald-400" />
                          <span className="text-xs font-medium uppercase tracking-wider text-[#006039] dark:text-emerald-400">
                            {i18n.language?.startsWith('de') ? 'Spruch des Tages' : 'Quote of the Day'}
                          </span>
                        </div>
                        {isLoggedIn && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={toggleQuoteFavorite}
                            className={`h-8 w-8 p-0 rounded-full transition-all ${isQuoteFavorite ? 'text-red-500 hover:text-red-600' : 'text-muted-foreground hover:text-red-500'}`}
                            title={isQuoteFavorite ? (i18n.language?.startsWith('de') ? 'Aus Favoriten entfernen' : 'Remove from favorites') : (i18n.language?.startsWith('de') ? 'Zu Favoriten hinzufügen' : 'Add to favorites')}
                          >
                            <Heart className={`h-4 w-4 ${isQuoteFavorite ? 'fill-current' : ''}`} />
                          </Button>
                        )}
                      </div>
                      <p className="text-lg sm:text-xl font-serif text-foreground italic leading-relaxed">
                        &bdquo;{dailyQuote}&ldquo;
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      )
    );

    const renderStatsSection = () => (
      <div key="stats-section" className="mb-6">
        <SectionHeader 
          sectionId="stats" 
          title={i18n.language?.startsWith('de') ? 'Statistiken' : 'Statistics'}
          icon={TrendingUp}
        />
        {sectionVisibility.stats && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4"
          >
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}>
              <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-200/50 dark:border-orange-800/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-500/20">
                      <Flame className="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{userStats.currentStreak}</p>
                      <p className="text-xs text-muted-foreground">{i18n.language?.startsWith('de') ? 'Tage in Folge' : 'Day Streak'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}>
          <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-200/50 dark:border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{userStats.totalSessions}</p>
                  <p className="text-xs text-muted-foreground">{i18n.language?.startsWith('de') ? 'Sessions' : 'Sessions'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }}>
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-200/50 dark:border-purple-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <Trophy className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{userStats.longestStreak}</p>
                  <p className="text-xs text-muted-foreground">{i18n.language?.startsWith('de') ? 'Längste Serie' : 'Best Streak'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 }}>
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-200/50 dark:border-blue-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Target className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{userStats.activePractices}</p>
                  <p className="text-xs text-muted-foreground">{i18n.language?.startsWith('de') ? 'Aktive Praktiken' : 'Active Practices'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
          </motion.div>
        )}
      </div>
    );

    const renderPremiumStatsSection = () => (
      <div key="premium-stats-section" className="mb-6">
        <SectionHeader 
          sectionId="premium_stats" 
          title={i18n.language?.startsWith('de') ? 'Dein Fortschritt' : 'Your Progress'}
          icon={TrendingUp}
        />
        {sectionVisibility.premium_stats && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <PremiumStatsCard />
          </motion.div>
        )}
      </div>
    );

    const renderMotivationSection = () => (
      <section key="motivation-section" className="py-6">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <SectionHeader 
              sectionId="motivation" 
              title={i18n.language?.startsWith('de') ? 'Tägliche Motivation' : 'Daily Motivation'}
              icon={Flame}
            />
            {sectionVisibility.motivation && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                <Card className="bg-gradient-to-r from-[#006039] to-emerald-600 text-white overflow-hidden relative">
                  <div className="absolute top-0 right-0 opacity-10">
                    <Sparkles className="h-64 w-64 -mt-16 -mr-16" />
                  </div>
                  <CardContent className="p-8 relative">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                      <div className="space-y-3">
                        <Badge variant="secondary" className="bg-white/20 text-white border-0">
                          <Zap className="h-3 w-3 mr-1" />
                          {lic.motivationBadge}
                        </Badge>
                        <h3 className="text-2xl font-serif font-bold">{lic.motivationTitle}</h3>
                        <p className="text-white/80 max-w-xl">{lic.motivationDescription}</p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Link to={lic.primaryButtonLink}>
                          <Button variant="secondary" size="lg" className="gap-2 w-full sm:w-auto text-sm sm:text-base whitespace-nowrap">
                            <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                            <span className="truncate">{lic.primaryButtonText}</span>
                          </Button>
                        </Link>
                        <Link to={lic.secondaryButtonLink}>
                          <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/20 bg-white/10 w-full sm:w-auto text-sm sm:text-base whitespace-nowrap">
                            <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                            <span className="truncate">{lic.secondaryButtonText}</span>
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </section>
    );

    const renderPracticesSection = () => (
      lic.showPractices && (
        <section key="practices-section" className="py-6">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <SectionHeader 
                sectionId="practices" 
                title={lic.practicesTitle}
                icon={Target}
              >
                <Link to="/practices" className="text-sm text-primary hover:underline flex items-center gap-1">
                  {i18n.language?.startsWith('de') ? 'Alle anzeigen' : 'View all'}
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </SectionHeader>
              {sectionVisibility.practices && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {userPractices.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {userPractices.slice(0, 6).map((practice, index) => (
                        <motion.div key={practice.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
                          <Link to="/practices">
                            <Card className="h-full hover:shadow-lg transition-all duration-300 group cursor-pointer border-2 hover:border-[#006039]/50">
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex-1">
                                    <h3 className="font-semibold text-foreground group-hover:text-[#006039] transition-colors line-clamp-1">
                                      {practice.practice_name || practice.practice_key}
                                    </h3>
                                    <p className="text-sm text-muted-foreground line-clamp-1">
                                  {practice.custom_goal || (i18n.language?.startsWith('de') ? 'Tägliche Praxis' : 'Daily practice')}
                                </p>
                              </div>
                              {practice.current_streak > 0 && (
                                <Badge variant="secondary" className="gap-1 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                                  <Flame className="h-3 w-3" />
                                  {practice.current_streak}
                                </Badge>
                              )}
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">{i18n.language?.startsWith('de') ? 'Fortschritt' : 'Progress'}</span>
                                <span className="font-medium">{practice.total_sessions || 0} Sessions</span>
                              </div>
                              <Progress value={Math.min((practice.total_sessions || 0) / 30 * 100, 100)} className="h-2" />
                            </div>
                            <div className="flex items-center justify-between mt-4 pt-3 border-t text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {i18n.language?.startsWith('de') ? 'Gestartet' : 'Started'}: {new Date(practice.start_date).toLocaleDateString()}
                              </span>
                              <span className="flex items-center gap-1">
                                <Award className="h-3 w-3" />
                                {i18n.language?.startsWith('de') ? 'Längste' : 'Best'}: {practice.longest_streak || 0}
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center">
                    <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-semibold mb-2">{i18n.language?.startsWith('de') ? 'Noch keine Praktiken gestartet' : 'No practices started yet'}</h3>
                    <p className="text-muted-foreground mb-4">{i18n.language?.startsWith('de') ? 'Beginne heute mit Deiner ersten Praktik!' : 'Start your first practice today!'}</p>
                    <Link to="/practices">
                      <Button>
                        <Play className="h-4 w-4 mr-2" />
                        {i18n.language?.startsWith('de') ? 'Praktiken entdecken' : 'Discover Practices'}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )
    );

    const renderQuickAccessSection = () => (
      lic.showQuickAccess && userFeatures.length > 0 && (
        <section key="quick-access-section" className="py-6">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <SectionHeader 
                sectionId="quick_access" 
                title={lic.quickAccessTitle}
                icon={Zap}
              >
                <Button variant="outline" size="sm" onClick={() => setIsFeatureSelectorOpen(true)} className="gap-2">
                  <Settings className="h-4 w-4" />
                  {i18n.language?.startsWith('de') ? 'Anpassen' : 'Customize'}
                </Button>
              </SectionHeader>
              {sectionVisibility.quick_access && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {userFeatures.map((feature, index) => (
                      <motion.div key={feature.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                        <Link to={feature.link}>
                          <Card className="h-full hover:shadow-lg transition-all duration-300 group cursor-pointer text-center">
                            <CardContent className="p-4">
                              <div className="text-4xl mb-2 group-hover:scale-110 transition-transform">{feature.icon}</div>
                              <h3 className="font-medium text-sm group-hover:text-[#006039] transition-colors">{getText(feature.title)}</h3>
                            </CardContent>
                          </Card>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )
    );

    // Pinboard Section Renderer
    const renderPinboardSection = () => (
      <section key="pinboard-section" className="py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader 
            sectionId="pinboard" 
            title={lic.pinboardTitle || (i18n.language?.startsWith('de') ? 'Meine Pinwand' : 'My Pinboard')}
            description={lic.pinboardDescription}
            icon={Pin}
          />
        </div>
        
        {sectionVisibility.pinboard && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Pinboard 
              title={null}
              language={i18n.language?.startsWith('de') ? 'de' : 'en'} 
            />
          </motion.div>
        )}
      </section>
    );

    // Section renderer map - stats now uses premium version
    const sectionRenderers = {
      quote: renderQuoteSection,
      stats: renderPremiumStatsSection,  // Upgraded: stats now shows premium dashboard
      premium_stats: renderPremiumStatsSection,
      motivation: renderMotivationSection,
      practices: renderPracticesSection,
      quick_access: renderQuickAccessSection,
      pinboard: renderPinboardSection
    };

    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <Navigation />

        {/* Custom CSS */}
        {config.custom_css?.global_styles && (
          <style dangerouslySetInnerHTML={{ __html: config.custom_css.global_styles }} />
        )}

        {/* Announcement Bar - Also for logged in users */}
        {config.announcement_bar?.is_active && (
          <div 
            className="w-full py-3 px-4 border-b"
            style={{
              backgroundColor: config.announcement_bar.background_color || '#006039',
              color: config.announcement_bar.text_color || '#ffffff'
            }}
          >
            <div className="container mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 flex-wrap">
              {((i18n.language?.startsWith('de') ? config.announcement_bar.message_de : config.announcement_bar.message_en) || '').trim() && (
                <span className="text-sm font-medium text-center">
                  {i18n.language?.startsWith('de') ? config.announcement_bar.message_de : config.announcement_bar.message_en}
                </span>
              )}
              
              {(config.announcement_bar.social_buttons || [])
                .filter(btn => btn.is_active)
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((button, idx) => {
                  const label = i18n.language?.startsWith('de') ? button.label_de : button.label_en;
                  if (!label || !button.url) return null;
                  
                  return (
                    <a
                      key={idx}
                      href={button.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 hover:shadow-lg"
                      style={{
                        backgroundColor: button.color || '#ffffff',
                        color: '#ffffff'
                      }}
                    >
                      {button.icon && <span className="text-lg">{button.icon}</span>}
                      <span>{label}</span>
                    </a>
                  );
                })}
            </div>
          </div>
        )}

        {/* Personalized Welcome Section with Greeting */}
        <section className="relative overflow-hidden py-12 lg:py-16">
          <div className="container mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-6xl mx-auto"
            >
              {/* Greeting - Always first */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-6">
                <div>
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground">
                    {getGreeting()}, {userName || 'Reisender'}! 👋
                  </h1>
                  <p className="text-lg text-muted-foreground mt-2">
                    {lic.greetingSubtitle}
                  </p>
                </div>
                
                {/* Dynamic CTA Buttons */}
                <div className="flex flex-wrap gap-3">
                  {lic.ctaButtons && lic.ctaButtons.length > 0 ? (
                    // Render dynamic buttons from config
                    lic.ctaButtons.filter(btn => btn.enabled !== false).map((btn) => {
                      // Map icon names to components
                      const iconMap = {
                        'Play': Play, 'Target': Target, 'Calendar': Calendar, 'Clock': Clock,
                        'BookOpen': BookOpen, 'Heart': Heart, 'Star': Star, 'Zap': Zap,
                        'FolderOpen': FolderOpen, 'Music': Music, 'Dumbbell': Dumbbell,
                        'Award': Award, 'Trophy': Trophy, 'Flame': Flame
                      };
                      const IconComponent = iconMap[btn.icon] || Play;
                      
                      return (
                        <Link key={btn.id} to={btn.link}>
                          <Button 
                            size="lg" 
                            variant={btn.variant || 'default'}
                            className="gap-2"
                          >
                            <IconComponent className="h-5 w-5" />
                            {getText(btn.text)}
                          </Button>
                        </Link>
                      );
                    })
                  ) : (
                    // Fallback: Original single button
                    <Link to="/practices">
                      <Button size="lg" className="gap-2">
                        <Play className="h-5 w-5" />
                        {lic.ctaButtonText}
                      </Button>
                    </Link>
                  )}
                </div>
              </div>

              {/* Render sections in configured order - only quote and stats inside the hero */}
              {lic.sectionOrder.filter(s => ['quote', 'stats', 'premium_stats'].includes(s)).map(sectionId => sectionRenderers[sectionId]?.())}
            </motion.div>
          </div>
        </section>

        {/* Render remaining sections outside the hero - including pinboard based on sectionOrder */}
        {lic.sectionOrder.filter(s => !['quote', 'stats', 'premium_stats'].includes(s)).map(sectionId => sectionRenderers[sectionId]?.())}

        {/* User Feature Selector */}
        <UserFeatureSelector
          isOpen={isFeatureSelectorOpen}
          onClose={() => setIsFeatureSelectorOpen(false)}
          onSave={() => fetchUserFeatures()}
        />

        <Footer />
      </div>
    );
  }

  // ========================================
  // GUEST VIEW - Marketing Landing Page
  // ========================================
  
  // Get SEO data
  const currentLang = i18n.language?.startsWith('de') ? 'de' : 'en';
  const seoTitle = currentLang === 'de' 
    ? (config.seo_config?.meta_title_de || 'AsceticLife - Achtsam leben')
    : (config.seo_config?.meta_title_en || 'AsceticLife - Mindful Living');
  const seoDescription = currentLang === 'de'
    ? (config.seo_config?.meta_description_de || 'Entdecke AsceticLife - Deine App für Meditation, Tagebuch und bewusstes Leben.')
    : (config.seo_config?.meta_description_en || 'Discover AsceticLife - Your app for meditation, journaling and conscious living.');
  const seoKeywords = currentLang === 'de'
    ? (config.seo_config?.meta_keywords_de || 'Meditation, Achtsamkeit, Minimalismus, Tagebuch, Wellness')
    : (config.seo_config?.meta_keywords_en || 'meditation, mindfulness, minimalism, journal, wellness');
  
  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="robots" content={config.seo_config?.robots || 'index, follow'} />
        
        {/* Open Graph */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content={config.seo_config?.og_type || 'website'} />
        {config.seo_config?.og_image && <meta property="og:image" content={config.seo_config.og_image} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content={config.seo_config?.twitter_card || 'summary_large_image'} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {config.seo_config?.og_image && <meta name="twitter:image" content={config.seo_config.og_image} />}
        
        {/* Canonical */}
        {config.seo_config?.canonical_url && <link rel="canonical" href={config.seo_config.canonical_url} />}
        
        {/* Structured Data for LLMs */}
        {config.seo_config?.structured_data && (
          <script type="application/ld+json">
            {JSON.stringify(config.seo_config.structured_data)}
          </script>
        )}
      </Helmet>

      <Navigation />

      {/* Custom CSS */}
      {config.custom_css?.global_styles && (
        <style dangerouslySetInnerHTML={{ __html: config.custom_css.global_styles }} />
      )}

      {/* Announcement Bar with Social Buttons */}
      {config.announcement_bar?.is_active && (
        <div 
          className="w-full py-3 px-4 border-b"
          style={{
            backgroundColor: config.announcement_bar.background_color || '#006039',
            color: config.announcement_bar.text_color || '#ffffff'
          }}
        >
          <div className="container mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 flex-wrap">
            {((i18n.language?.startsWith('de') ? config.announcement_bar.message_de : config.announcement_bar.message_en) || '').trim() && (
              <span className="text-sm font-medium text-center">
                {i18n.language?.startsWith('de') ? config.announcement_bar.message_de : config.announcement_bar.message_en}
              </span>
            )}
            
            {(config.announcement_bar.social_buttons || [])
              .filter(btn => btn.is_active)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((button, idx) => {
                const label = i18n.language?.startsWith('de') ? button.label_de : button.label_en;
                if (!label || !button.url) return null;
                
                return (
                  <a
                    key={idx}
                    href={button.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 hover:shadow-lg"
                    style={{
                      backgroundColor: button.color || '#ffffff',
                      color: '#ffffff'
                    }}
                  >
                    {button.icon && <span className="text-lg">{button.icon}</span>}
                    <span>{label}</span>
                  </a>
                );
              })}
          </div>
        </div>
      )}

      {/* Hero Section - Backend Configurable */}
      <section className="relative overflow-hidden bg-gradient-to-b from-background to-primary-glow/10">
        <div className="container mx-auto px-4 py-20 lg:py-32">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center space-y-8"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Badge variant="secondary" className="px-4 py-2 text-sm">
                <Compass className="w-4 h-4 mr-2" />
                {getText(config.hero_section?.badge_text) || t('home.badge')}
              </Badge>
            </motion.div>

            {/* Main Heading - from Backend */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-serif font-bold text-foreground leading-tight">
              {getText(config.hero_section?.title) || t('home.title')}
            </h1>

            {/* Subheading - from Backend with proper line breaks */}
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed whitespace-pre-line">
              {getText(config.hero_section?.subtitle) || t('home.subtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Link to="/practices">
                <Button size="lg" variant="zen" className="shadow-medium">
                  <Mountain className="mr-2 h-5 w-5" />
                  {t('home.explorePractices')}
                </Button>
              </Link>
              <Link to="/journal">
                <Button size="lg" variant="outline">
                  <BookOpen className="mr-2 h-5 w-5" />
                  {t('home.startJournaling')}
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-6 pt-12 max-w-3xl mx-auto"
            >
              {stats.map((stat, index) => (
                <div key={index} className="space-y-1">
                  <p className="text-3xl font-serif font-bold text-primary">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 opacity-20">
          <Leaf className="h-32 w-32 text-primary animate-float" />
        </div>
        <div className="absolute bottom-20 right-10 opacity-20">
          <Leaf className="h-24 w-24 text-primary animate-float" style={{ animationDelay: '1s' }} />
        </div>
      </section>

      {/* Core Principles Section */}
      <section id="principles" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-4 mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground">
              {getText(config?.principles_section?.title) || t('home.principles.title')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {getText(config?.principles_section?.subtitle) || t('home.principles.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {principles.map((principle, index) => {
              const Icon = principle.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-all duration-300 group cursor-pointer border-2 hover:border-ascetic-400 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm">
                    <CardHeader className="space-y-4">
                      <div className="p-3 bg-[#006039]/10 dark:bg-[#006039]/20 rounded-lg w-fit group-hover:bg-[#006039] group-hover:scale-110 transition-all duration-300">
                        <Icon className="h-8 w-8 text-[#006039] dark:text-emerald-400 group-hover:text-white" />
                      </div>
                      <CardTitle className="font-serif text-xl">{principle.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="leading-relaxed">
                        {principle.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Backend-Configurable Features Section */}
      {config.features_section && config.features_section.features?.length > 0 && (
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center space-y-4 mb-12"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground">
                {getText(config.features_section.title)}
              </h2>
              {config.features_section.subtitle && (
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed whitespace-pre-line">
                  {getText(config.features_section.subtitle)}
                </p>
              )}
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {config.features_section.features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card 
                    className={`h-full hover:shadow-elevated transition-smooth ${
                      feature.extended_description && getText(feature.extended_description) 
                        ? 'cursor-pointer' 
                        : ''
                    }`}
                    onClick={() => handleFeatureClick(feature)}
                  >
                    <CardHeader className="text-center space-y-4">
                      <div className="text-5xl mx-auto">{feature.icon}</div>
                      <CardTitle className="text-xl font-semibold">
                        {getText(feature.title)}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-center leading-relaxed">
                        {getText(feature.description)}
                      </CardDescription>
                      {feature.extended_description && getText(feature.extended_description) && (
                        <p className="text-center text-xs text-primary mt-3">
                          Klicke für mehr Details →
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Practices Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-4 mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground">
              {getText(config?.practices_section?.title) || t('home.practices.title')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {getText(config?.practices_section?.subtitle) || t('home.practices.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            {practices.map((practice, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="hover:shadow-elevated transition-smooth group cursor-pointer h-full flex flex-col">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="font-serif text-xl group-hover:text-primary transition-smooth">
                        {practice.title}
                      </CardTitle>
                      <Badge variant="outline">{practice.difficulty}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    <CardDescription className="leading-relaxed flex-1 mb-4">
                      {practice.description}
                    </CardDescription>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground pt-4 border-t">
                      <span className="flex items-center gap-1">
                        <Target className="h-4 w-4" />
                        {practice.duration}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/practices">
              <Button size="lg" variant="zen">
                {getText(config?.practices_section?.button_text) || (i18n.language === 'de' ? 'Alle Praktiken anzeigen' : 'View All Practices')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-4 mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground">
              {pricingConfig ? (i18n.language === 'de' ? pricingConfig.title_de : pricingConfig.title_en) : (i18n.language === 'de' ? 'Wähle Deinen Plan' : 'Choose Your Plan')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {pricingConfig ? (i18n.language === 'de' ? pricingConfig.subtitle_de : pricingConfig.subtitle_en) : (i18n.language === 'de' 
                ? 'Starte noch heute und transformiere Dein Leben mit AsceticLife.' 
                : 'Start today and transform your life with AsceticLife.')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingConfig?.plans?.map((plan, index) => {
              const isYearly = plan.plan_id === 'yearly';
              const isMonthly = plan.plan_id === 'monthly';
              const planName = i18n.language === 'de' ? plan.name_de : plan.name_en;
              const periodText = i18n.language === 'de' ? plan.period_de : plan.period_en;
              const vatText = pricingConfig ? (i18n.language === 'de' ? pricingConfig.vat_notice_de : pricingConfig.vat_notice_en) : 'inkl. MwSt.';
              const badgeText = i18n.language === 'de' ? plan.badge_de : plan.badge_en;
              const ctaText = i18n.language === 'de' ? plan.cta_text_de : plan.cta_text_en;
              
              // Calculate monthly equivalent for yearly plan
              const monthlyEquivalent = isYearly ? (plan.price / 12).toFixed(2).replace('.', ',') : null;
              
              return (
                <motion.div
                  key={plan.plan_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className={`h-full border-2 relative overflow-hidden ${
                    isYearly ? 'border-amber-400 shadow-lg' : 'border-[#006039]'
                  }`}>
                    {/* Badge */}
                    {(badgeText || (isMonthly && !badgeText)) && (
                      <div className={`absolute top-0 right-0 text-white text-xs px-3 py-1 rounded-bl-lg ${
                        isYearly ? 'bg-amber-500 font-semibold' : 'bg-[#006039]'
                      }`}>
                        {badgeText || (isMonthly ? (i18n.language === 'de' ? 'Flexibel' : 'Flexible') : '')}
                      </div>
                    )}
                    
                    <CardHeader className="text-center pb-2">
                      <CardTitle className={`text-xl font-medium ${isYearly ? 'text-amber-600' : 'text-[#006039]'}`}>
                        {planName}
                      </CardTitle>
                      <div className="pt-4">
                        <span className="text-4xl font-bold">{plan.price}{plan.currency}</span>
                        <span className="text-muted-foreground">/{periodText?.replace('pro ', '')}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{vatText}</p>
                      {monthlyEquivalent && (
                        <p className="text-sm text-amber-600 font-medium">
                          = {monthlyEquivalent}€/{i18n.language === 'de' ? 'Monat' : 'month'}
                        </p>
                      )}
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <ul className="space-y-3 text-sm">
                        {plan.features?.map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-center gap-2">
                            <CheckCircle2 className={`h-4 w-4 ${isYearly ? 'text-amber-500' : 'text-[#006039]'}`} />
                            <span className={feature.highlight ? 'font-semibold' : ''}>
                              {i18n.language === 'de' ? feature.text_de : feature.text_en}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <Link to="/subscription" className="block">
                        <Button className={`w-full ${
                          isYearly ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-[#006039] hover:bg-[#004d2e]'
                        }`}>
                          {ctaText}
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-muted-foreground"
          >
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-[#006039]" />
              {i18n.language === 'de' ? 'Sichere Zahlung' : 'Secure Payment'}
            </div>
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-[#006039]" />
              {i18n.language === 'de' ? 'Sofortiger Zugang' : 'Instant Access'}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-[#006039]" />
              {i18n.language === 'de' ? 'Jederzeit kündbar' : 'Cancel Anytime'}
            </div>
          </motion.div>
          
          <div className="text-center mt-6">
            <Link to="/features" className="text-sm text-[#006039] dark:text-emerald-400 hover:underline font-medium inline-flex items-center gap-1">
              {i18n.language === 'de' ? 'Alle Features im Detail vergleichen' : 'Compare all features in detail'}
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#006039] to-emerald-600 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center space-y-6"
          >
            <h2 className="text-3xl sm:text-4xl font-serif font-bold">
              {getText(config.cta_section?.title) || t('home.cta.title')}
            </h2>
            <p className="text-lg text-white/80">
              {getText(config.cta_section?.description) || t('home.cta.description')}
            </p>
            <Link to="/subscription">
              <Button size="lg" variant="secondary" className="mt-4">
                {i18n.language === 'de' ? 'Jetzt Premium werden' : 'Go Premium Now'}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Feature Detail Dialog */}
      <Dialog open={isFeatureDialogOpen} onOpenChange={setIsFeatureDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-4xl">{selectedFeature?.icon}</span>
              <DialogTitle className="text-2xl">
                {getText(selectedFeature?.title)}
              </DialogTitle>
            </div>
            <DialogDescription className="text-base leading-relaxed pt-2">
              {getText(selectedFeature?.description)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-foreground leading-relaxed whitespace-pre-line">
              {getText(selectedFeature?.extended_description)}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default DynamicHomePage;
